import { useState, useEffect, useCallback } from "react";
import {
  getVersions,
  getRnCVersions,
  getSBJobDescription,
  getHistoryList,
  publishJD,
  unPublishJD,
  editJD,
} from "Api/apiService";
import { userHasEntitlement } from "Utils/parseEntitlements";
import { getFetchErrorMessage } from "Utils/jobDetailsPageState";
import { getLatestVersionRow } from "Utils/versionUtils";
import useAuthStore from "Store/useAuthStore";
import useJobsStore from "Store/useJobsStore";
import useEntitlementsStore from "Store/useEntitlementsStore";
import { JD_STATUSES } from "constants/jobs.constants";

/**
 * useJobDetailsV2
 *
 * V2-specific fork of useJobDetails.
 * - The original ("base") JD is not fetched at all: when the Difference Checker has nothing to compare
 *   it shows the version already on the page.
 * - comparisonSummary and fetchComparisonSummary are removed (unused in V2).
 * - Which version is the latest comes from the backend (`is_latest` on the /versions rows), never
 *   from sorting or comparing version numbers here.
 */
const useJobDetailsV2 = () => {
  const selectedJob = useJobsStore((state) => state.selectedJob);
  const setSelectedJob = useJobsStore((state) => state.setSelectedJob);
  const email = useAuthStore((s) => s.email);
  const accessToken = useAuthStore((s) => s.accessToken);
  const { entitlementsData, profileRole } = useEntitlementsStore();

  // Core state
  const [versionData, setVersionData] = useState([]);
  // Drives the version dropdown only (P-1 through current range). Everything
  // else on the page reads the richer versionData from /api/versions.
  const [versionDropdownOptions, setVersionDropdownOptions] = useState([]);
  const [selectedJdVersion, setSelectedJdVersion] = useState(1.0);
  const [sbUniqueId, setSbUniqueId] = useState('');

  // Workflow states
  const [mongoWorkflowStatus, setMongoWorkflowStatus] = useState("");
  const [reviewHistory, setReviewHistory] = useState([]);

  // Job descriptions and evaluations
  const [sbJd, setSbJd] = useState("");
  const [editableMarkdown, setEditableMarkdown] = useState("");

  // Automation data
  const [skillsData, setSkillsData] = useState([]);
  const [overallPotential, setOverallPotential] = useState(null);

  // UI state
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [blurredJD, setBlurredJD] = useState(false);
  const [jdPublished, setJdPublished] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [showToast, setShowToast] = useState(false);
  const [sbJdStatus, setSBJdStatus] = useState("");

  // Files and ownership
  const [publishedJDfiles, setPublishedJDfiles] = useState({ pdfUrl: "", docxUrl: "" });
  const [currentOwner, setCurrentOwner] = useState('');
  const [assignedApproverName, setAssignedApproverName] = useState('');

  // JD Owner
  const [rootOwnerEmail, setRootOwnerEmail] = useState("");
  // Backend's answer to "what may I do to this JD?" (owner, latest version, per-action permissions)
  const [jdOwnership, setJdOwnership] = useState(null);

  // Helper functions
  const resetJdData = useCallback(() => {
    setSbJd("");
    setEditableMarkdown("");
    setMongoWorkflowStatus("");
    setReviewHistory([]);
    setRootOwnerEmail("");
    setAssignedApproverName("");
  }, []);

  const getIdToUse = useCallback((fallbackId) => {
    return sbUniqueId || fallbackId;
  }, [sbUniqueId]);

  const handleError = useCallback((error, message) => {
    console.error(message, error);
    setError(getFetchErrorMessage(error, message));
  }, []);

  const processPublishedFiles = useCallback((documents) => {
    if (!Array.isArray(documents)) return;

    const files = { pdfUrl: "", docxUrl: "" };
    documents.forEach((file) => {
      if (file.fileType === "pdf") {
        files.pdfUrl = file.signedUrl;
      } else if (file.fileType === "docx") {
        files.docxUrl = file.signedUrl;
      }
    });
    setPublishedJDfiles(files);
  }, []);

  const fetchSBJobDescription = useCallback(async (targetSbUniqueId) => {
    const idToUse = targetSbUniqueId || selectedJob?.sb_unique_id;
    if (!idToUse) return;

    setLoading(true);
    setError(null);
    resetJdData();

    try {
      const response = await getSBJobDescription(idToUse, email, accessToken);

      if (!response) {
        setError('No JD found or invalid response.');
        return;
      }

      if (response.assigned_approver_name) {
        setAssignedApproverName(response.assigned_approver_name);
      }

      if (response.current_owner !== undefined) {
        setRootOwnerEmail(response.current_owner);
      }

      setJdOwnership(response.permissions ? {
        permissions: response.permissions,
        isOwner: response.is_owner,
        isUnowned: response.is_unowned,
        ownerName: response.owner_name,
      } : null);

      if (response.jd_status) {
        setJdPublished(response.jd_status === JD_STATUSES.PUBLISHED)
        setMongoWorkflowStatus(response.jd_status);
      }
      if (response.review_history) {
        setReviewHistory(response.review_history);
      }

      if (response.documents) {
        processPublishedFiles(response.documents);
      }

      if (response.job_description) {
        setSbJd(response.job_description);
        setEditableMarkdown(response.job_description);
      }

      if (response.current_owner !== null) {
        setCurrentOwner(response.current_owner);
      }

      if (response.is_preview && response.message) {
        setBlurredJD(true);
      }

      if (response.automation_potential) {
        const { automation_potential } = response.automation_potential;
        const overall = parseInt(response.automation_potential.overall_role_automation_potential);
        setSkillsData(automation_potential);
        setOverallPotential(overall);
      }

      const { jd_status } = response;
      setSBJdStatus(jd_status);

    } catch (err) {
      handleError(err, 'Failed to fetch JD');
    } finally {
      setLoading(false);
    }
  }, [
    selectedJob,
    email,
    accessToken,
    resetJdData,
    processPublishedFiles,
    handleError
  ]);

  const fetchVersions = useCallback(async () => {
    if (!selectedJob?.job_id) return;
    setError(null);

    try {
      const useRnC = userHasEntitlement(entitlementsData, profileRole, "jobs_library-version", "audit");
      const response = useRnC
        ? await getRnCVersions(email, selectedJob.job_id, accessToken)
        : await getVersions(email, selectedJob.job_id, accessToken);

      // Version dropdown is driven separately, by the approval history range
      // (P-1 through current). Failure here must not block the page.
      try {
        const historyList = await getHistoryList(selectedJob.job_id);
        setVersionDropdownOptions(Array.isArray(historyList) ? historyList : []);
      } catch (historyErr) {
        console.error('Failed to fetch version dropdown list:', historyErr);
        setVersionDropdownOptions([]);
      }

      if (response && response.length > 0) {
        setVersionData(response);

        // The backend marks the latest version this viewer may see (is_latest). The first row is only a
        // fallback for a response that carries no mark (an older backend); the list is newest first.
        const trueLatestVersion = getLatestVersionRow(response) ?? response[0];
        const currentIdInUse = getIdToUse(selectedJob.sb_unique_id);

        // If selectedJob is a sentinel (set by restore effect with no full data),
        // populate it now so the UI has all fields (jd_status, job_title, etc.)
        if (!selectedJob?.jd_status) {
          setSelectedJob(trueLatestVersion);
        }

        setSelectedJdVersion(trueLatestVersion.version);
        if (trueLatestVersion.sb_unique_id !== currentIdInUse) {
          setSbUniqueId(trueLatestVersion.sb_unique_id);
        }
        await fetchSBJobDescription(trueLatestVersion.sb_unique_id);
      } else {
        setError('No versions found or invalid response.');
      }
    } catch (err) {
      handleError(err, 'Failed to fetch versions');
    } finally {
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- pre-existing on dev, not changed by this PR
  }, [email, selectedJob, accessToken, getIdToUse, handleError, entitlementsData, profileRole, fetchSBJobDescription]);

  const refreshData = useCallback(async (targetSbUniqueId) => {
    await fetchVersions();
    await fetchSBJobDescription(targetSbUniqueId);
  }, [fetchVersions, fetchSBJobDescription]);

  // Action functions
  const publishJd = useCallback(async () => {
    const idToUse = getIdToUse(selectedJob?.sb_unique_id);
    if (!idToUse) return;

    setLoading(true);
    setError(null);

    try {
      const response = await publishJD(idToUse, email);

      if (response.success && response.status === JD_STATUSES.PUBLISHED && response.sb_unique_id) {
        setJdPublished(true);
        setSbUniqueId(response.sb_unique_id);
        resetJdData();

        await fetchSBJobDescription(response.sb_unique_id);
        await fetchVersions(response.sb_unique_id);
        setSelectedJdVersion(response.version);
      } else if (
        response.success &&
        response.status === JD_STATUSES.UNPUBLISHED &&
        response.message === "No new changes to publish. Edit to continue"
      ) {
        setJdPublished(false);
        setToastMessage("Make changes before republishing.");
        setShowToast(true);
      } else {
        setError('Invalid response.');
      }
    } catch (err) {
      handleError(err, 'Failed to publish JD');
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- pre-existing on dev, not changed by this PR
  }, [
    getIdToUse,
    selectedJob,
    email,
    accessToken,
    resetJdData,
    fetchSBJobDescription,
    fetchVersions,
    handleError
  ]);

  const unPublishJd = useCallback(async () => {
    const idToUse = getIdToUse(selectedJob?.sb_unique_id);
    if (!idToUse) return;

    setLoading(true);
    setError(null);

    try {
      const response = await unPublishJD(idToUse, email, accessToken);
      if (response.success && response.status !== JD_STATUSES.PUBLISHED) {
        setJdPublished(false);
        setCurrentOwner(response.current_owner);

        await refreshData(idToUse);
      } else {
        setError('Invalid response.');
      }
    } catch (err) {
      handleError(err, 'Failed to unpublish JD');
    } finally {
      setLoading(false);
    }
  }, [getIdToUse, selectedJob, email, accessToken, handleError, refreshData]);

  const saveEditedJd = useCallback(async (updatedJdCurrent) => {
    const idToUse = getIdToUse(selectedJob?.sb_unique_id);
    if (!idToUse || !selectedJob?.job_code) return;

    setLoading(true);
    setError(null);

    try {
      const response = await editJD(idToUse, email, updatedJdCurrent, selectedJob.job_code);

      if (response.success && response.sb_unique_id) {
        resetJdData();

        await fetchSBJobDescription(response.sb_unique_id);
        await fetchVersions(response.sb_unique_id);

        setSbUniqueId(response.sb_unique_id);
        setSelectedJdVersion(response.version);

        return response;
      } else {
        setError('Invalid response.');
      }
    } catch (err) {
      handleError(err, 'Failed to save edited JD');
    } finally {
      setLoading(false);
    }
  }, [
    getIdToUse,
    selectedJob,
    email,
    resetJdData,
    fetchSBJobDescription,
    fetchVersions,
    handleError
  ]);


  const handleSbUniqueIdChange = useCallback((newSbUniqueId) => {
    resetJdData();
    fetchSBJobDescription(newSbUniqueId);
  }, [resetJdData, fetchSBJobDescription]);

  // Effects
  useEffect(() => {
    if (selectedJob?.jd_status) {
      setJdPublished(selectedJob.jd_status === JD_STATUSES.PUBLISHED);
    }
  }, [selectedJob]);

  // Clear stale version data immediately when job changes so loading spinner shows at once
  useEffect(() => {
    if (selectedJob?.job_id) {
      setVersionData([]);
      resetJdData();
    }
  }, [selectedJob?.job_id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!selectedJob?.job_id) return;
    fetchVersions();
  }, [selectedJob?.job_id]); // eslint-disable-line react-hooks/exhaustive-deps

  return {
    // Data
    versionData,
    versionDropdownOptions,
    selectedJdVersion,
    setSelectedJdVersion,
    sbJd,
    mongoWorkflowStatus,
    reviewHistory,
    sbUniqueId,
    setSbUniqueId,
    editableMarkdown,
    setEditableMarkdown,
    skillsData,
    overallPotential,
    publishedJDfiles,
    currentOwner,
    setCurrentOwner,
    assignedApproverName,
    setAssignedApproverName,

    // UI State
    loading,
    error,
    blurredJD,
    jdPublished,
    toastMessage,
    setToastMessage,
    showToast,
    setShowToast,
    sbJdStatus,

    // Actions
    handleSbUniqueIdChange,
    publishJd,
    unPublishJd,
    saveEditedJd,
    refreshData,

    // JD Owner
    rootOwnerEmail,
    setRootOwnerEmail,
    jdOwnership,
  };
};

export default useJobDetailsV2;
